import { Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription, firstValueFrom } from 'rxjs';
import { PrimengImports } from '../../../../../shared/primeng/primeng.imports';
import { fieldErrorMessage, YES_NO_OPTIONS } from '../../models/property-intake.model';
import { LeadService } from '../../../../../core/services/lead.service';
import { FeasibilityQuestion, FeasibilityQuestionOption } from '../../../../../core/models/lead.model';

/** Question codes this step renders, and which form control each one binds to —
 *  the one piece of hardcoding left, since Reactive Forms controls have to be
 *  declared up front by the parent (property-intake.component.ts). Everything
 *  about *what the question says and what the options are* comes from the API. */
const CODE_TO_CONTROL: Record<string, string> = {
  INTAKE_INTENDED_USE: 'intendedUse',
  INTAKE_INTENDED_USE_OTHER: 'intendedUseOtherDescription',
  INTAKE_PRIMARY_INTEREST: 'primaryInterest',
  INTAKE_ABOUT_ADU: 'aboutAdu',
  INTAKE_NEEDS_FINANCING: 'needsFinancing',
  INTAKE_NOTES: 'notes',
};

@Component({
  selector: 'krc-intended-use-step',
  standalone: true,
  imports: [ReactiveFormsModule, PrimengImports],
  templateUrl: './intended-use-step.component.html',
  styleUrl: './intended-use-step.component.scss',
})
export class IntendedUseStepComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly leadService = inject(LeadService);
  private intendedUseSubscription?: Subscription;

  readonly form = input.required<FormGroup>();

  readonly loadingQuestions = signal(true);
  readonly questionsFailed = signal(false);
  private readonly questionByCode = signal<Map<string, FeasibilityQuestion>>(new Map());

  readonly yesNoOptions = YES_NO_OPTIONS;

  // Signals, not plain properties — PrimeNG's SelectButton reads [options] once and
  // doesn't reliably notice a later plain-property reassignment landing after an
  // async fetch resolves; a signal guarantees the template (and PrimeNG's own
  // internal OnPush check) actually sees the update.
  readonly intendedUseQuestionText = signal('Intended Use');
  readonly primaryInterestQuestionText = signal('Primary Interest');
  readonly aboutAduQuestionText = signal('Where from did you hear about Kore ADU?');
  // readonly needsFinancingQuestionText = signal('Do you need financing?');
  readonly notesQuestionText = signal('Additional Notes');

  readonly intendedUseOptions = signal<FeasibilityQuestionOption[]>([]);
  readonly primaryInterestOptions = signal<FeasibilityQuestionOption[]>([]);
  readonly aboutAduOptions = signal<FeasibilityQuestionOption[]>([]);

  constructor() {
    this.loadQuestions();

    effect(() => {
      const form = this.form();
      this.intendedUseSubscription?.unsubscribe();

      const intendedUseControl = form.get('intendedUse');
      this.syncOtherDescriptionValidator(form, intendedUseControl?.value ?? []);
      this.intendedUseSubscription = intendedUseControl?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(values => this.syncOtherDescriptionValidator(form, values));
    });
  }

  /** Question text + options for every code this step cares about, fetched once
   *  from the real question bank instead of a hardcoded file kept in sync by hand. */
  private async loadQuestions(): Promise<void> {
    this.loadingQuestions.set(true);
    this.questionsFailed.set(false);
    try {
      const questions = await firstValueFrom(this.leadService.getFeasibilityQuestions('INTAKE', 'INTENDED_USE'));
      const byCode = new Map(questions.map(q => [q.questionCode, q]));
      this.questionByCode.set(byCode);

      const intendedUse = byCode.get('INTAKE_INTENDED_USE');
      if (intendedUse) { this.intendedUseQuestionText.set(intendedUse.questionText); this.intendedUseOptions.set(intendedUse.options ?? []); }

      const primaryInterest = byCode.get('INTAKE_PRIMARY_INTEREST');
      if (primaryInterest) { this.primaryInterestQuestionText.set(primaryInterest.questionText); this.primaryInterestOptions.set(primaryInterest.options ?? []); }

      const aboutAdu = byCode.get('INTAKE_ABOUT_ADU');
      if (aboutAdu) { this.aboutAduQuestionText.set(aboutAdu.questionText); this.aboutAduOptions.set(aboutAdu.options ?? []); }

      // const needsFinancing = byCode.get('INTAKE_NEEDS_FINANCING');
      // if (needsFinancing) this.needsFinancingQuestionText.set(needsFinancing.questionText);

      const notes = byCode.get('INTAKE_NOTES');
      if (notes) this.notesQuestionText.set(notes.questionText);
    } catch {
      // Question bank is unreachable — the form stays usable with its built-in
      // fallback labels above rather than blocking the homeowner entirely.
      this.questionsFailed.set(true);
    } finally {
      this.loadingQuestions.set(false);
    }
  }

  /** Every question fetched for this step, keyed by its own questionCode — used by
   *  the parent (property-intake.component.ts) to build the generic answers[]
   *  payload without needing to know question IDs itself. */
  get questionByControlName(): Map<string, string> {
    const map = new Map<string, string>();
    for (const [code, control] of Object.entries(CODE_TO_CONTROL)) map.set(control, code);
    return map;
  }

  get intendedUseTouched(): boolean {
    const control = this.form().get('intendedUse');
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  get showOtherDescription(): boolean {
    const values: string[] = this.form().get('intendedUse')?.value ?? [];
    return values.includes('other');
  }

  isInvalid(controlName: string): boolean {
    const control = this.form().get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  errorFor(controlName: string, messages: Record<string, string>): string | null {
    return fieldErrorMessage(this.form().get(controlName), messages);
  }

  private syncOtherDescriptionValidator(form: FormGroup, intendedUseValues: string[]): void {
    const control = form.get('intendedUseOtherDescription');
    if (!control) return;

    const validators = intendedUseValues.includes('other')
      ? [Validators.required, Validators.maxLength(300)]
      : [Validators.maxLength(300)];
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }
}
